import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { grammarService, normalizeLesson, type GrammarLesson } from "@/services/grammarService";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  PlusCircle, 
  Search, 
  Edit, 
  Trash2, 
  ArrowLeft, 
  Save, 
  Eye, 
  EyeOff, 
  Check, 
  Loader2, 
  AlertTriangle,
  HelpCircle,
  FileText,
  Plus,
  Minus,
  FileJson,
  Upload,
  X
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "@/components/ui/dialog";


function validateImportData(data: any): { lessons: Omit<GrammarLesson, "id" | "created_at" | "updated_at">[]; errors: string[] } {
  const errors: string[] = [];
  const lessons: Omit<GrammarLesson, "id" | "created_at" | "updated_at">[] = [];

  if (!Array.isArray(data)) {
    return { lessons: [], errors: ["O JSON fornecido deve ser um array de lições."] };
  }

  data.forEach((item, idx) => {
    const itemErrors: string[] = [];
    const prefix = `Item [${idx + 1}] (${item.title || "Sem título"})`;

    // 1. Mandatory fields
    if (typeof item.title !== "string" || !item.title.trim()) {
      itemErrors.push("O campo 'title' é obrigatório e deve ser uma string.");
    }
    if (typeof item.slug !== "string" || !item.slug.trim()) {
      itemErrors.push("O campo 'slug' é obrigatório e deve ser uma string.");
    }
    if (typeof item.level !== "string" || !["A1", "A2", "B1", "B2"].includes(item.level)) {
      itemErrors.push("O campo 'level' é obrigatório e deve ser um dos valores: 'A1', 'A2', 'B1', 'B2'.");
    }
    if (typeof item.order_index !== "number") {
      itemErrors.push("O campo 'order_index' é obrigatório e deve ser um número.");
    }

    // Convert legacy string content format to modern object
    if (typeof item.content === "string") {
      item.content = {
        intro: "",
        rule: item.content,
        structure: "",
        examples: [],
        commonMistakes: [],
        tefTip: ""
      };
    }

    // 2. Content structure
    if (!item.content || typeof item.content !== "object") {
      itemErrors.push("O campo 'content' é obrigatório.");
    } else {
      const { intro, rule, structure, examples, commonMistakes, tefTip } = item.content;
      if (intro !== undefined && typeof intro !== "string") {
        itemErrors.push("O subcampo 'content.intro' deve ser uma string.");
      }
      if (rule !== undefined && typeof rule !== "string") {
        itemErrors.push("O subcampo 'content.rule' deve ser uma string.");
      }
      if (structure !== undefined && typeof structure !== "string") {
        itemErrors.push("O subcampo 'content.structure' deve ser uma string.");
      }
      if (examples !== undefined && !Array.isArray(examples)) {
        itemErrors.push("O subcampo 'content.examples' deve ser um array.");
      } else if (Array.isArray(examples)) {
        examples.forEach((ex: any, exIdx: number) => {
          if (typeof ex !== "string" && (typeof ex !== "object" || typeof ex.sentence !== "string")) {
            itemErrors.push(`O exemplo [${exIdx + 1}] deve ser uma string ou um objeto com a chave 'sentence' do tipo string.`);
          }
        });
      }
      if (commonMistakes !== undefined && !Array.isArray(commonMistakes)) {
        itemErrors.push("O subcampo 'content.commonMistakes' deve ser um array de desvios.");
      } else if (Array.isArray(commonMistakes)) {
        commonMistakes.forEach((m: any, mIdx: number) => {
          if (typeof m !== "object" || (typeof m.incorrect !== "string" && typeof m.wrong !== "string") || typeof m.correct !== "string") {
            itemErrors.push(`O desvio comum [${mIdx + 1}] deve ser um objeto com as chaves 'incorrect'/'wrong' e 'correct' do tipo string.`);
          }
        });
      }
      if (tefTip !== undefined && typeof tefTip !== "string") {
        itemErrors.push("O subcampo 'content.tefTip' deve ser uma string.");
      }
    }

    // 3. Dialogue structure validation
    if (item.dialogue !== undefined && item.dialogue !== null) {
      if (typeof item.dialogue !== "object") {
        itemErrors.push("O campo 'dialogue' deve ser um objeto.");
      } else {
        const { title: dTitle, context, characters, lines, vocabularyNotes } = item.dialogue;
        if (dTitle !== undefined && typeof dTitle !== "string") {
          itemErrors.push("O campo 'dialogue.title' deve ser uma string.");
        }
        if (context !== undefined && typeof context !== "string") {
          itemErrors.push("O campo 'dialogue.context' deve ser uma string.");
        }
        if (characters !== undefined && !Array.isArray(characters)) {
          itemErrors.push("O campo 'dialogue.characters' deve ser um array de strings.");
        }
        if (lines !== undefined && !Array.isArray(lines)) {
          itemErrors.push("O campo 'dialogue.lines' deve ser um array de falas.");
        } else if (Array.isArray(lines)) {
          lines.forEach((l: any, lIdx: number) => {
            if (typeof l !== "object" || typeof l.speaker !== "string" || typeof l.text !== "string") {
              itemErrors.push(`O diálogo linha [${lIdx + 1}] deve ser um objeto com as chaves 'speaker' e 'text' do tipo string.`);
            }
          });
        }
        if (vocabularyNotes !== undefined && !Array.isArray(vocabularyNotes)) {
          itemErrors.push("O campo 'dialogue.vocabularyNotes' deve ser um array de notas de vocabulário.");
        } else if (Array.isArray(vocabularyNotes)) {
          vocabularyNotes.forEach((vn: any, vnIdx: number) => {
            if (typeof vn !== "object" || typeof vn.term !== "string" || typeof vn.meaning !== "string") {
              itemErrors.push(`O vocabulário nota [${vnIdx + 1}] deve ser um objeto com as chaves 'term' e 'meaning' do tipo string.`);
            }
          });
        }
      }
    }

    // 4. Flashcards structure validation
    if (item.flashcards !== undefined && item.flashcards !== null) {
      if (!Array.isArray(item.flashcards)) {
        itemErrors.push("O campo 'flashcards' deve ser um array.");
      } else {
        item.flashcards.forEach((f: any, fIdx: number) => {
          if (typeof f !== "object" || typeof f.front !== "string" || typeof f.back !== "string") {
            itemErrors.push(`O flashcard [${fIdx + 1}] deve ser um objeto com as chaves 'front' e 'back' do tipo string.`);
          }
          if (f.difficulty !== undefined && !["easy", "medium", "hard"].includes(f.difficulty)) {
            itemErrors.push(`O flashcard [${fIdx + 1}] deve ter dificuldade 'easy', 'medium' ou 'hard'.`);
          }
        });
      }
    }

    // 5. Exercises structure
    if (!Array.isArray(item.exercises)) {
      itemErrors.push("O campo 'exercises' é obrigatório e deve ser um array.");
    } else {
      item.exercises.forEach((ex: any, exIdx: number) => {
        if (!["multiple_choice", "fill_blank", "transformation"].includes(ex.type)) {
          itemErrors.push(`Exercício [${exIdx + 1}]: o campo 'type' deve ser um dos: 'multiple_choice', 'fill_blank', 'transformation'.`);
        }
        if (typeof ex.prompt !== "string" || !ex.prompt.trim()) {
          itemErrors.push(`Exercício [${exIdx + 1}]: o campo 'prompt' é obrigatório.`);
        }
        if (ex.type === "multiple_choice") {
          if (!Array.isArray(ex.options) || ex.options.length < 2) {
            itemErrors.push(`Exercício [${exIdx + 1}]: para o tipo 'multiple_choice', o campo 'options' deve ser um array com pelo menos 2 opções.`);
          }
        }
        if (typeof ex.answer !== "string" || !ex.answer.trim()) {
          itemErrors.push(`Exercício [${exIdx + 1}]: o campo 'answer' é obrigatório e deve conter a resposta correta.`);
        }
        if (ex.explanation !== undefined && typeof ex.explanation !== "string") {
          itemErrors.push(`Exercício [${exIdx + 1}]: o campo 'explanation' deve ser uma string.`);
        }
      });
    }

    if (itemErrors.length > 0) {
      errors.push(`${prefix}:\n  - ${itemErrors.join("\n  - ")}`);
    } else {
      // Dialogue normalization
      let dialogueValue = null;
      if (item.dialogue) {
        dialogueValue = {
          title: typeof item.dialogue.title === "string" ? item.dialogue.title.trim() : "",
          context: typeof item.dialogue.context === "string" ? item.dialogue.context.trim() : "",
          characters: Array.isArray(item.dialogue.characters) ? item.dialogue.characters.map((c: string) => c.trim()).filter(Boolean) : [],
          lines: Array.isArray(item.dialogue.lines) 
            ? item.dialogue.lines.map((l: any) => ({
                speaker: typeof l.speaker === "string" ? l.speaker.trim() : "",
                text: typeof l.text === "string" ? l.text.trim() : ""
              }))
            : [],
          vocabularyNotes: Array.isArray(item.dialogue.vocabularyNotes)
            ? item.dialogue.vocabularyNotes.map((vn: any) => ({
                term: typeof vn.term === "string" ? vn.term.trim() : "",
                meaning: typeof vn.meaning === "string" ? vn.meaning.trim() : ""
              }))
            : []
        };
      }

      // Flashcards normalization
      let flashcardsValue = [];
      if (Array.isArray(item.flashcards)) {
        flashcardsValue = item.flashcards.map((f: any) => ({
          front: typeof f.front === "string" ? f.front.trim() : "",
          back: typeof f.back === "string" ? f.back.trim() : "",
          category: typeof f.category === "string" ? f.category.trim() : undefined,
          difficulty: ["easy", "medium", "hard"].includes(f.difficulty) ? f.difficulty : undefined
        }));
      }

      // Exercises normalization
      const exercisesValue = item.exercises.map((ex: any) => {
        const baseEx: any = {
          type: ex.type,
          prompt: ex.prompt.trim(),
          answer: ex.answer.trim(),
          explanation: typeof ex.explanation === "string" ? ex.explanation.trim() : ""
        };
        if (ex.type === "multiple_choice") {
          baseEx.options = Array.isArray(ex.options) ? ex.options.map((o: string) => o.trim()).filter(Boolean) : [];
        }
        return baseEx;
      });

      lessons.push({
        title: item.title.trim(),
        slug: item.slug.trim(),
        level: item.level,
        order_index: item.order_index,
        category: typeof item.category === "string" ? item.category.trim() : null,
        description: typeof item.description === "string" ? item.description.trim() : null,
        is_published: typeof item.is_published === "boolean" ? item.is_published : false,
        is_locked_by_default: true,
        estimated_minutes: typeof item.estimated_minutes === "number" ? item.estimated_minutes : 10,
        dialogue: dialogueValue,
        flashcards: flashcardsValue,
        content: {
          intro: typeof item.content.intro === "string" ? item.content.intro.trim() : "",
          rule: typeof item.content.rule === "string" ? item.content.rule.trim() : "",
          structure: typeof item.content.structure === "string" ? item.content.structure.trim() : "",
          examples: Array.isArray(item.content.examples) 
            ? item.content.examples.map((e: any) => {
                if (typeof e === "string") {
                  return { sentence: e.trim(), translation: "", note: "" };
                }
                return {
                  sentence: typeof e.sentence === "string" ? e.sentence.trim() : "",
                  translation: typeof e.translation === "string" ? e.translation.trim() : "",
                  note: typeof e.note === "string" ? e.note.trim() : ""
                };
              })
            : [],
          commonMistakes: Array.isArray(item.content.commonMistakes) 
            ? item.content.commonMistakes.map((m: any) => ({
                wrong: typeof (m.wrong || m.incorrect) === "string" ? (m.wrong || m.incorrect).trim() : "",
                correct: typeof m.correct === "string" ? m.correct.trim() : "",
                explanation: typeof m.explanation === "string" ? m.explanation.trim() : ""
              }))
            : [],
          tefTip: typeof item.content.tefTip === "string" ? item.content.tefTip.trim() : ""
        },
        exercises: exercisesValue
      });
    }
  });

  return { lessons, errors };
}


export default function AdminGrammar() {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  // List view states
  const [lessons, setLessons] = useState<GrammarLesson[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Editor states
  const [isEditing, setIsEditing] = useState(false);
  const [currentLesson, setCurrentLesson] = useState<Partial<GrammarLesson> | null>(null);
  const [saving, setSaving] = useState(false);
  const [jsonError, setJsonError] = useState<string | null>(null);

  // Form fields
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [level, setLevel] = useState("A1");
  const [category, setCategory] = useState("");
  const [orderIndex, setOrderIndex] = useState(1);
  const [description, setDescription] = useState("");
  const [isPublished, setIsPublished] = useState(false);
  const [estimatedMinutes, setEstimatedMinutes] = useState(10);
  
  // Nested content fields
  const [intro, setIntro] = useState("");
  const [rule, setRule] = useState("");
  const [structure, setStructure] = useState("");
  const [examples, setExamples] = useState<{ sentence: string; translation: string; note: string }[]>([]);
  const [commonMistakes, setCommonMistakes] = useState<{ wrong: string; correct: string; explanation: string }[]>([]);
  const [tefTip, setTefTip] = useState("");
  const [dialogueJson, setDialogueJson] = useState("null");
  const [exercisesJson, setExercisesJson] = useState("[]");
  const [flashcardsJson, setFlashcardsJson] = useState("[]");

  // Batch Import states
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importJson, setImportJson] = useState("");
  const [parsedLessons, setParsedLessons] = useState<Omit<GrammarLesson, "id" | "created_at" | "updated_at">[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  const [hasValidated, setHasValidated] = useState(false);
  const [importAsExpressionOrale, setImportAsExpressionOrale] = useState(false);
  const [activeTab, setActiveTab] = useState<"grammar" | "eo">("grammar");


  useEffect(() => {
    loadLessons();
  }, []);

  async function loadLessons() {
    try {
      setLoading(true);
      const fetched = await grammarService.fetchLessons();
      setLessons(fetched);
      setSelectedIds([]); // Clear selection when list is reloaded
    } catch (err) {
      console.error("Failed to load lessons:", err);
    } finally {
      setLoading(false);
    }
  }

  const toggleSelectAll = () => {
    const filteredIds = filteredLessons.map(l => l.id).filter((id): id is string => !!id);
    const allFilteredSelected = filteredIds.every(id => selectedIds.includes(id));
    
    if (allFilteredSelected) {
      setSelectedIds(prev => prev.filter(id => !filteredIds.includes(id)));
    } else {
      setSelectedIds(prev => {
        const newSelection = [...prev];
        filteredIds.forEach(id => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
        });
        return newSelection;
      });
    }
  };

  const toggleSelectLesson = (id: string) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    const msg = `Tem certeza que deseja excluir as ${selectedIds.length} lições selecionadas? Esta ação é irreversível e excluirá todo o conteúdo associado.`;
    if (!window.confirm(msg)) return;

    setIsBulkDeleting(true);
    try {
      const success = await grammarService.deleteLessons(selectedIds);
      if (success) {
        setSelectedIds([]);
        loadLessons();
      } else {
        alert("Erro ao excluir as lições selecionadas.");
      }
    } catch (err: any) {
      console.error(err);
      alert(`Erro na exclusão em lote: ${err.message || err}`);
    } finally {
      setIsBulkDeleting(false);
    }
  };

  // Auto-generate slug from title
  const handleTitleChange = (newTitle: string) => {
    setTitle(newTitle);
    if (!currentLesson?.id) {
      // Only auto-generate slug for new lessons
      const generated = newTitle
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "") // remove accents
        .replace(/[^a-z0-9\s-]/g, "") // remove special chars
        .trim()
        .replace(/\s+/g, "-");
      setSlug(generated);
    }
  };

  const handleEditClick = (lesson: GrammarLesson) => {
    const normalized = normalizeLesson(lesson);
    
    setCurrentLesson(lesson);
    setTitle(normalized.title);
    setSlug(normalized.slug);
    setLevel(normalized.level);
    setCategory(normalized.category || "");
    setOrderIndex(normalized.order_index);
    setDescription(normalized.description || "");
    setIsPublished(normalized.is_published);
    setEstimatedMinutes(normalized.estimated_minutes || 10);
    
    // Content mapping
    setIntro(normalized.content?.intro || "");
    setRule(normalized.content?.rule || "");
    setStructure(normalized.content?.structure || "");
    setExamples((normalized.content?.examples as { sentence: string; translation: string; note: string }[]) || []);
    setCommonMistakes((normalized.content?.commonMistakes as { wrong: string; correct: string; explanation: string }[]) || []);
    setTefTip(normalized.content?.tefTip || "");
    setDialogueJson(JSON.stringify(normalized.dialogue, null, 2));
    setExercisesJson(JSON.stringify(normalized.exercises || [], null, 2));
    setFlashcardsJson(JSON.stringify(normalized.flashcards || [], null, 2));

    setJsonError(null);
    setIsEditing(true);
  };

  const handleCreateClick = () => {
    setCurrentLesson(null);
    setTitle("");
    setSlug("");
    setLevel("A1");
    setCategory(activeTab === "eo" ? "Expression Orale" : "");
    
    // Default next order index
    const nextIdx = lessons.length > 0 ? Math.max(...lessons.map(l => l.order_index)) + 1 : 1;
    setOrderIndex(nextIdx);
    setDescription("");
    setIsPublished(false);
    setEstimatedMinutes(10);
    
    // Reset nested fields
    setIntro("");
    setRule("");
    setStructure("");
    setExamples([]);
    setCommonMistakes([]);
    setTefTip("");
    setDialogueJson("null");
    setFlashcardsJson("[]");
    
    // Basic exercise structure template
    const defaultExercise = [
      {
        type: "multiple_choice",
        prompt: "Choisissez la forme correcte...",
        options: ["Option A", "Option B", "Option C", "Option D"],
        answer: "Option A",
        explanation: "Explication didactique..."
      }
    ];
    setExercisesJson(JSON.stringify(defaultExercise, null, 2));

    setJsonError(null);
    setIsEditing(true);
  };

  const handleAddExample = () => {
    setExamples(prev => [...prev, { sentence: "", translation: "", note: "" }]);
  };

  const handleRemoveExample = (idx: number) => {
    setExamples(prev => prev.filter((_, i) => i !== idx));
  };

  const handleExampleChange = (idx: number, field: "sentence" | "translation" | "note", value: string) => {
    setExamples(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const handleAddMistake = () => {
    setCommonMistakes(prev => [...prev, { wrong: "", correct: "", explanation: "" }]);
  };

  const handleRemoveMistake = (idx: number) => {
    setCommonMistakes(prev => prev.filter((_, i) => i !== idx));
  };

  const handleMistakeChange = (idx: number, field: "wrong" | "correct" | "explanation", value: string) => {
    setCommonMistakes(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const handleSave = async () => {
    setJsonError(null);

    // Validate Slug is not empty
    if (!slug.trim()) {
      setJsonError("O slug da lição é obrigatório.");
      return;
    }

    // Validate Exercises JSON
    let parsedExercises = [];
    try {
      parsedExercises = JSON.parse(exercisesJson);
      if (!Array.isArray(parsedExercises)) {
        setJsonError("Os exercícios devem ser um array JSON.");
        return;
      }
    } catch (err: any) {
      setJsonError(`Erro de sintaxe JSON nos exercícios: ${err.message}`);
      return;
    }

    // Validate Dialogue JSON
    let parsedDialogue = null;
    try {
      parsedDialogue = JSON.parse(dialogueJson);
      if (parsedDialogue !== null && (typeof parsedDialogue !== "object" || Array.isArray(parsedDialogue))) {
        setJsonError("O diálogo deve ser um objeto JSON ou null.");
        return;
      }
    } catch (err: any) {
      setJsonError(`Erro de sintaxe JSON no diálogo: ${err.message}`);
      return;
    }

    // Validate Flashcards JSON
    let parsedFlashcards = [];
    try {
      parsedFlashcards = JSON.parse(flashcardsJson);
      if (!Array.isArray(parsedFlashcards)) {
        setJsonError("Os flashcards devem ser um array JSON.");
        return;
      }
    } catch (err: any) {
      setJsonError(`Erro de sintaxe JSON nos flashcards: ${err.message}`);
      return;
    }

    setSaving(true);

    const payload: Omit<GrammarLesson, "id" | "created_at" | "updated_at"> = {
      title: title.trim(),
      slug: slug.trim(),
      level,
      category: category.trim() || null,
      order_index: Number(orderIndex),
      description: description.trim() || null,
      is_published: isPublished,
      is_locked_by_default: true,
      estimated_minutes: Number(estimatedMinutes),
      dialogue: parsedDialogue,
      flashcards: parsedFlashcards,
      content: {
        intro: intro.trim(),
        rule: rule.trim(),
        structure: structure.trim() || undefined,
        examples: examples.filter(e => e.sentence.trim()),
        commonMistakes: commonMistakes.filter(m => m.wrong.trim() || m.correct.trim()),
        tefTip: tefTip.trim()
      },
      exercises: parsedExercises
    };

    try {
      let result;
      if (currentLesson?.id) {
        result = await grammarService.updateLesson(currentLesson.id, payload);
      } else {
        result = await grammarService.createLesson(payload);
      }

      if (result) {
        setIsEditing(false);
        loadLessons();
      } else {
        setJsonError("Erro ao salvar as informações no banco de dados.");
      }
    } catch (err: any) {
      setJsonError(`Erro no salvamento: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Tem certeza que deseja excluir esta lição? Esta ação é irreversível.")) return;
    try {
      const success = await grammarService.deleteLesson(id);
      if (success) {
        loadLessons();
      } else {
        alert("Erro ao excluir lição.");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleValidateImport = () => {
    setValidationErrors([]);
    setParsedLessons([]);
    setHasValidated(false);

    if (!importJson.trim()) {
      setValidationErrors(["Por favor, cole um JSON válido antes de validar."]);
      return;
    }

    try {
      const parsed = JSON.parse(importJson);
      const { lessons: validatedLessons, errors } = validateImportData(parsed);
      
      const allErrors = [...errors];

      if (errors.length === 0) {
        // Check duplicate slugs inside the imported JSON itself
        const importedSlugs = validatedLessons.map(l => l.slug);
        const duplicateImportedSlugs = importedSlugs.filter((s, idx) => importedSlugs.indexOf(s) !== idx);
        const uniqueDuplicateImported = Array.from(new Set(duplicateImportedSlugs));
        if (uniqueDuplicateImported.length > 0) {
          allErrors.push(`O JSON importado contém slugs duplicados dentro do próprio lote: ${uniqueDuplicateImported.join(", ")}`);
        }

        // Check duplicate slugs against existing lessons in database (local state 'lessons')
        const existingDuplicateSlugs = validatedLessons
          .filter(vl => lessons.some(l => l.slug === vl.slug))
          .map(vl => vl.slug);
        if (existingDuplicateSlugs.length > 0) {
          allErrors.push(`Já existe uma lição com este slug no banco de dados: ${existingDuplicateSlugs.join(", ")}`);
        }
      }

      if (allErrors.length > 0) {
        setValidationErrors(allErrors);
      } else {
        setParsedLessons(validatedLessons);
      }
      setHasValidated(true);
    } catch (err: any) {
      setValidationErrors([`Erro de sintaxe JSON: ${err.message}`]);
      setHasValidated(true);
    }
  };

  const handleImportSubmit = async () => {
    if (parsedLessons.length === 0) return;

    setImporting(true);
    try {
      // Force draft status (is_published = false) and override category if toggle is active
      const processedLessons = parsedLessons.map(lesson => ({
        ...lesson,
        is_published: false,
        category: importAsExpressionOrale ? "Expression Orale" : lesson.category
      }));

      const res = await grammarService.bulkImportLessons(processedLessons, "ignore");

      if (res.success) {
        setIsImportOpen(false);
        // Reset modal fields
        setImportJson("");
        setParsedLessons([]);
        setValidationErrors([]);
        setHasValidated(false);
        setImportAsExpressionOrale(false);
        alert(`Sucesso! Importação concluída. ${res.count} lições foram importadas como rascunho.`);
        loadLessons();
      } else {
        alert(`Erro na importação: ${res.error || "Erro desconhecido"}`);
      }
    } catch (err: any) {
      alert(`Falha no processo de importação: ${err.message}`);
    } finally {
      setImporting(false);
    }
  };


  // Filter lessons by query and active tab
  const filteredLessons = lessons.filter(l => {
    // 1. Filter by category tab
    if (activeTab === "grammar") {
      if (l.category === "Expression Orale") return false;
    } else {
      if (l.category !== "Expression Orale") return false;
    }

    // 2. Filter by search query
    return (
      l.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (l.category && l.category.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  });

  if (!isAdmin) {
    return null; // Secured by AdminRoute, but fail-safe
  }

  return (
    <div className="space-y-6 pb-16 max-w-7xl mx-auto animate-in fade-in duration-500 text-left">
      
      {/* Top Header Panel */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-slate-100 pb-6">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-primary to-indigo-600 flex items-center justify-center text-white shadow-md">
              <FileText className="w-5 h-5" />
            </div>
            <span>{isEditing ? "Editar Conteúdo de Gramática" : "Conteúdos de Gramática"}</span>
          </h1>
          <p className="text-slate-500 mt-1.5 text-sm font-medium">
            {isEditing 
              ? "Modifique explicações, exemplos, dicas e exercícios da lição." 
              : "Cadastro de lições, definição de níveis e publicação de conteúdo curricular."
            }
          </p>
        </div>

        <div className="flex gap-2">
          {isEditing ? (
            <Button
              variant="ghost"
              onClick={() => setIsEditing(false)}
              className="rounded-xl border border-slate-200 text-slate-700 font-bold text-xs gap-1.5 h-10 px-4 bg-slate-50 hover:bg-slate-100"
            >
              <ArrowLeft className="w-4 h-4 text-slate-500" />
              Cancelar
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => setIsImportOpen(true)}
                className="rounded-xl border border-slate-200 text-slate-700 font-bold text-xs gap-1.5 h-10 px-4 bg-white hover:bg-slate-50 shadow-sm"
              >
                <FileJson className="w-4 h-4 text-slate-500" />
                Importar Lições
              </Button>
              <Button
                onClick={handleCreateClick}
                className="rounded-xl bg-primary hover:bg-primary/95 text-white font-bold text-xs gap-1.5 h-10 px-4 shadow-sm"
              >
                <PlusCircle className="w-4 h-4" />
                Nova Lição
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate("/admin")}
                className="rounded-xl border border-slate-200 text-slate-700 font-bold text-xs gap-1.5 h-10 px-4 bg-slate-50 hover:bg-slate-100"
              >
                <ArrowLeft className="w-4 h-4 text-slate-500" />
                Painel Admin
              </Button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className="py-24 text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <h4 className="font-bold text-slate-700 text-base">Buscando lições de gramática...</h4>
        </div>
      ) : !isEditing ? (
        
        /* 1. LIST VIEW OF LESSONS */
        <Card className="border-none shadow-sm bg-white rounded-2xl overflow-hidden">
          <div className="p-5 border-b border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex flex-col md:flex-row md:items-center gap-4 flex-1">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Pesquisar por título ou categoria..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="pl-10 h-10 rounded-xl border-slate-200"
                />
              </div>

              {/* Segment control tabs */}
              <div className="flex gap-1 bg-slate-100/80 p-1 rounded-xl w-fit shrink-0 border border-slate-200/20">
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab("grammar");
                    setSelectedIds([]);
                  }}
                  className={`rounded-lg font-bold text-xs px-4 py-2 transition-all ${
                    activeTab === "grammar" 
                      ? "bg-white text-slate-800 shadow-sm" 
                      : "text-slate-500 hover:text-slate-800 bg-transparent"
                  }`}
                >
                  Lições de Gramática
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab("eo");
                    setSelectedIds([]);
                  }}
                  className={`rounded-lg font-bold text-xs px-4 py-2 transition-all ${
                    activeTab === "eo" 
                      ? "bg-white text-slate-800 shadow-sm" 
                      : "text-slate-500 hover:text-slate-800 bg-transparent"
                  }`}
                >
                  Expression Orale (EO)
                </button>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {selectedIds.length > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDelete}
                  disabled={isBulkDeleting}
                  className="rounded-xl font-bold text-xs gap-1.5 h-10 px-4 shadow-sm bg-red-600 hover:bg-red-700 text-white"
                >
                  {isBulkDeleting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  Excluir Selecionadas ({selectedIds.length})
                </Button>
              )}
              <Badge className="bg-slate-50 border-slate-200 text-slate-700 font-bold text-xs px-2.5 py-1">
                Total: {filteredLessons.length}
              </Badge>
            </div>
          </div>
          
          <CardContent className="p-0">
            {filteredLessons.length === 0 ? (
              <div className="py-12 text-center text-slate-400 italic text-xs font-semibold">
                Nenhuma lição de gramática cadastrada
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100 text-slate-400 font-extrabold uppercase tracking-wider text-[9px]">
                      <th className="px-4 py-3.5 w-10 text-center">
                        <input
                          type="checkbox"
                          checked={filteredLessons.length > 0 && filteredLessons.every(l => l.id && selectedIds.includes(l.id))}
                          onChange={toggleSelectAll}
                          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer align-middle"
                        />
                      </th>
                      <th className="px-6 py-3.5 w-12 text-center">Ordem</th>
                      <th className="px-6 py-3.5">Título</th>
                      <th className="px-6 py-3.5 w-24">Nível</th>
                      <th className="px-6 py-3.5">Categoria</th>
                      <th className="px-6 py-3.5 w-28 text-center">Status</th>
                      <th className="px-6 py-3.5 w-28 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-semibold text-slate-700">
                    {filteredLessons.map((l) => (
                      <tr key={l.id} className={`hover:bg-slate-50/50 transition-colors ${l.id && selectedIds.includes(l.id) ? "bg-slate-50" : ""}`}>
                        <td className="px-4 py-3.5 text-center">
                          <input
                            type="checkbox"
                            checked={!!l.id && selectedIds.includes(l.id)}
                            onChange={() => l.id && toggleSelectLesson(l.id)}
                            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer align-middle"
                          />
                        </td>
                        <td className="px-6 py-3.5 font-mono text-center font-bold text-slate-500 text-xs">
                          {l.order_index}
                        </td>
                        <td className="px-6 py-3.5">
                          <span className="font-extrabold text-slate-900 text-[13px]">{l.title}</span>
                          <span className="block font-mono text-[10px] text-slate-400 mt-0.5">/{l.slug}</span>
                        </td>
                        <td className="px-6 py-3.5">
                          <Badge className={`text-[10px] font-black uppercase border-none px-2 py-0.5 rounded ${
                            l.level.startsWith("A") ? "bg-emerald-50 text-emerald-700" :
                            l.level.startsWith("B1") ? "bg-blue-50 text-blue-700" : "bg-purple-50 text-purple-700"
                          }`}>
                            {l.level}
                          </Badge>
                        </td>
                        <td className="px-6 py-3.5 text-slate-500 text-xs font-semibold">
                          {l.category || "Sem Categoria"}
                        </td>
                        <td className="px-6 py-3.5 text-center">
                          {l.is_published ? (
                            <Badge className="bg-emerald-100 text-emerald-800 border-none text-[9px] font-black uppercase px-2 py-0.5 rounded flex items-center gap-1.5 w-fit mx-auto">
                              <Eye className="w-3 h-3" />
                              Publicado
                            </Badge>
                          ) : (
                            <Badge className="bg-slate-100 text-slate-600 border-none text-[9px] font-black uppercase px-2 py-0.5 rounded flex items-center gap-1.5 w-fit mx-auto">
                              <EyeOff className="w-3 h-3" />
                              Rascunho
                            </Badge>
                          )}
                        </td>
                        <td className="px-6 py-3.5 text-right space-x-1.5">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleEditClick(l)}
                            className="w-8 h-8 rounded-lg text-slate-500 hover:text-primary hover:bg-slate-100"
                            title="Editar"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => l.id && handleDelete(l.id)}
                            className="w-8 h-8 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50"
                            title="Excluir"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

      ) : (

        /* 2. FULL SCREEN CREATOR/EDITOR FORM */
        <div className="grid gap-6 md:grid-cols-3">
          
          {/* Main Content Columns */}
          <div className="md:col-span-2 space-y-6">
            
            {/* Metadata Card */}
            <Card className="border border-slate-100 shadow-xs rounded-2xl p-6 bg-white space-y-4">
              <h3 className="font-extrabold text-sm text-slate-800 border-b border-slate-50 pb-2">Informações Curriculares</h3>
              
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="title" className="text-xs font-bold text-slate-600">Título da Lição *</Label>
                  <Input
                    id="title"
                    type="text"
                    value={title}
                    onChange={e => handleTitleChange(e.target.value)}
                    placeholder="Ex: Le présent de l’indicatif"
                    required
                    className="h-10 rounded-xl"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="slug" className="text-xs font-bold text-slate-600">Slug único *</Label>
                  <Input
                    id="slug"
                    type="text"
                    value={slug}
                    onChange={e => setSlug(e.target.value)}
                    placeholder="Ex: le-present-de-l-indicatif"
                    required
                    className="h-10 rounded-xl font-mono text-xs"
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <Label htmlFor="level" className="text-xs font-bold text-slate-600">Nível *</Label>
                  <select
                    id="level"
                    value={level}
                    onChange={e => setLevel(e.target.value)}
                    className="flex h-10 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="A1">A1</option>
                    <option value="A2">A2</option>
                    <option value="B1">B1</option>
                    <option value="B2">B2</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="category" className="text-xs font-bold text-slate-600">Categoria</Label>
                  <Input
                    id="category"
                    type="text"
                    value={category}
                    onChange={e => setCategory(e.target.value)}
                    placeholder="Ex: Temps de l'indicatif"
                    className="h-10 rounded-xl"
                  />
                  <div className="flex items-center gap-1.5 mt-1">
                    <input
                      id="isExpressionOraleSingle"
                      type="checkbox"
                      checked={category === "Expression Orale"}
                      onChange={e => {
                        setCategory(e.target.checked ? "Expression Orale" : "");
                      }}
                      className="h-3.5 w-3.5 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                    />
                    <label htmlFor="isExpressionOraleSingle" className="text-[10px] font-extrabold text-slate-500 cursor-pointer select-none">
                      Definir como lição de Expression Orale (EO)
                    </label>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="orderIndex" className="text-xs font-bold text-slate-600">Ordem de Exibição *</Label>
                  <Input
                    id="orderIndex"
                    type="number"
                    value={orderIndex}
                    onChange={e => setOrderIndex(Number(e.target.value))}
                    min={1}
                    required
                    className="h-10 rounded-xl"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="estimatedMinutes" className="text-xs font-bold text-slate-600">Tempo Estimado (minutos) *</Label>
                <Input
                  id="estimatedMinutes"
                  type="number"
                  value={estimatedMinutes}
                  onChange={e => setEstimatedMinutes(Number(e.target.value))}
                  min={1}
                  required
                  className="h-10 rounded-xl"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="description" className="text-xs font-bold text-slate-600">Breve Descrição</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Ex: Domine o tempo mais importante da fala e escrita francesa..."
                  rows={2}
                  className="rounded-xl"
                />
              </div>

              <div className="flex items-center gap-3 bg-slate-50 p-3.5 rounded-xl border border-slate-100">
                <input
                  id="isPublished"
                  type="checkbox"
                  checked={isPublished}
                  onChange={e => setIsPublished(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                />
                <Label htmlFor="isPublished" className="text-xs font-extrabold text-slate-700 cursor-pointer select-none">
                  Publicar lição imediatamente (torna-se visível e desbloqueável para alunos)
                </Label>
              </div>
            </Card>

            {/* Core Explanations Card */}
            <Card className="border border-slate-100 shadow-xs rounded-2xl p-6 bg-white space-y-4">
              <h3 className="font-extrabold text-sm text-slate-800 border-b border-slate-50 pb-2">Explicação da Lição (Francês)</h3>
              
              <div className="space-y-1.5">
                <Label htmlFor="intro" className="text-xs font-bold text-slate-600">Introdução / Contextualização</Label>
                <Textarea
                  id="intro"
                  value={intro}
                  onChange={e => setIntro(e.target.value)}
                  placeholder="Introduza o assunto e quando utilizá-lo de forma geral..."
                  rows={3}
                  className="rounded-xl"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="rule" className="text-xs font-bold text-slate-600">Regra Principal / Tabela de Conjugações</Label>
                <Textarea
                  id="rule"
                  value={rule}
                  onChange={e => setRule(e.target.value)}
                  placeholder="Escreva a regra gramatical principal e as desinências..."
                  rows={4}
                  className="rounded-xl font-mono text-xs"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="structure" className="text-xs font-bold text-slate-600">Estrutura Destacada (Fórmula Gramatical)</Label>
                <Input
                  id="structure"
                  type="text"
                  value={structure}
                  onChange={e => setStructure(e.target.value)}
                  placeholder="Ex: Sujet + verbe au présent + complément"
                  className="h-10 rounded-xl"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="tefTip" className="text-xs font-bold text-slate-600">Dica Prática para o Exame TEF</Label>
                <Textarea
                  id="tefTip"
                  value={tefTip}
                  onChange={e => setTefTip(e.target.value)}
                  placeholder="Qual a relevância desse tema no exame TEF? Onde utilizá-lo nas seções?"
                  rows={3}
                  className="rounded-xl"
                />
              </div>
            </Card>

            {/* Examples Builder Card */}
            <Card className="border border-slate-100 shadow-xs rounded-2xl p-6 bg-white space-y-4">
              <div className="flex items-center justify-between border-b border-slate-50 pb-2">
                <h3 className="font-extrabold text-sm text-slate-800">Exemplos Práticos</h3>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAddExample}
                  className="h-8 rounded-lg text-[10px] font-extrabold gap-1"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Adicionar Exemplo
                </Button>
              </div>

              {examples.length === 0 ? (
                <p className="text-slate-400 text-xs italic font-semibold py-4 text-center">Nenhum exemplo prático registrado ainda.</p>
              ) : (
                <div className="space-y-4">
                  {examples.map((ex, idx) => (
                    <div key={idx} className="p-4 rounded-xl border border-indigo-100 bg-indigo-50/10 space-y-3 relative">
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => handleRemoveExample(idx)}
                        className="w-7 h-7 rounded-lg text-rose-500 hover:text-rose-700 hover:bg-rose-100/50 absolute top-2 right-2 flex items-center justify-center p-0"
                      >
                        <Minus className="w-4 h-4" />
                      </Button>

                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="space-y-1.5">
                          <Label className="text-[10px] font-extrabold text-indigo-700">Frase em Francês *</Label>
                          <Input
                            type="text"
                            value={ex.sentence}
                            onChange={e => handleExampleChange(idx, "sentence", e.target.value)}
                            placeholder="Ex: Je mange une pomme."
                            required
                            className="h-9 rounded-lg border-indigo-200 text-xs font-semibold"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-[10px] font-extrabold text-slate-700">Tradução em Português</Label>
                          <Input
                            type="text"
                            value={ex.translation}
                            onChange={e => handleExampleChange(idx, "translation", e.target.value)}
                            placeholder="Ex: Eu como uma maçã."
                            className="h-9 rounded-lg border-slate-200 text-xs font-semibold"
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-[10px] font-extrabold text-slate-500">Nota Gramatical / Dica de Pronúncia</Label>
                        <Input
                          type="text"
                          value={ex.note}
                          onChange={e => handleExampleChange(idx, "note", e.target.value)}
                          placeholder="Ex: 'mange' vem do verbo manger (1º grupo)..."
                          className="h-9 rounded-lg border-slate-200 text-xs font-semibold"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Common Mistakes Builder Card */}
            <Card className="border border-slate-100 shadow-xs rounded-2xl p-6 bg-white space-y-4">
              <div className="flex items-center justify-between border-b border-slate-50 pb-2">
                <h3 className="font-extrabold text-sm text-slate-800">Tabela de Erros Comuns</h3>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAddMistake}
                  className="h-8 rounded-lg text-[10px] font-extrabold gap-1"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Adicionar Erro
                </Button>
              </div>

              {commonMistakes.length === 0 ? (
                <p className="text-slate-400 text-xs italic font-semibold py-4 text-center">Nenhum desvio comum registrado ainda.</p>
              ) : (
                <div className="space-y-4">
                  {commonMistakes.map((mistake, idx) => (
                    <div key={idx} className="p-4 rounded-xl border border-rose-100 bg-rose-50/10 space-y-3 relative">
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => handleRemoveMistake(idx)}
                        className="w-7 h-7 rounded-lg text-rose-500 hover:text-rose-700 hover:bg-rose-100/50 absolute top-2 right-2 flex items-center justify-center p-0"
                      >
                        <Minus className="w-4 h-4" />
                      </Button>

                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="space-y-1.5">
                          <Label className="text-[10px] font-extrabold text-rose-700">Forma Incorreta *</Label>
                          <Input
                            type="text"
                            value={mistake.wrong}
                            onChange={e => handleMistakeChange(idx, "wrong", e.target.value)}
                            placeholder="Ex: Beaucoup des personnes"
                            required
                            className="h-9 rounded-lg border-rose-200 text-xs font-semibold"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-[10px] font-extrabold text-emerald-700">Forma Correta *</Label>
                          <Input
                            type="text"
                            value={mistake.correct}
                            onChange={e => handleMistakeChange(idx, "correct", e.target.value)}
                            placeholder="Ex: Beaucoup de personnes"
                            required
                            className="h-9 rounded-lg border-emerald-200 text-xs font-semibold"
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-[10px] font-extrabold text-slate-500">Explicação do erro</Label>
                        <Input
                          type="text"
                          value={mistake.explanation}
                          onChange={e => handleMistakeChange(idx, "explanation", e.target.value)}
                          placeholder="Explique o desvio em português..."
                          className="h-9 rounded-lg border-slate-200 text-xs font-semibold"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

          </div>

          {/* Sidebar Editor Columns */}
          <div className="space-y-6">
            
            {/* Action Bar Card */}
            <Card className="border border-slate-100 shadow-xs rounded-2xl p-6 bg-white space-y-4">
              <h3 className="font-extrabold text-sm text-slate-800 border-b border-slate-50 pb-2">Ações do Administrador</h3>
              
              <Button
                onClick={handleSave}
                disabled={saving || !title.trim()}
                className="w-full h-11 bg-primary hover:bg-primary/95 text-white font-bold rounded-xl text-xs flex items-center justify-center gap-2 shadow-md transition-all"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Salvando dados...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Salvar Lição
                  </>
                )}
              </Button>

              <Button
                variant="outline"
                type="button"
                onClick={() => setIsEditing(false)}
                className="w-full h-11 border-slate-200 hover:bg-slate-50 text-slate-600 font-bold rounded-xl text-xs flex items-center justify-center gap-2 transition-all"
              >
                Descartar alterações
              </Button>

              {jsonError && (
                <div className="p-3 rounded-xl bg-red-50 border border-red-100 text-red-700 flex items-start gap-2 text-xs font-semibold leading-relaxed">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <div>{jsonError}</div>
                </div>
              )}
            </Card>

            {/* JSON Exercise Card */}
            <Card className="border border-slate-100 shadow-xs rounded-2xl p-6 bg-white space-y-4">
              <div className="border-b border-slate-50 pb-2 flex items-center justify-between">
                <h3 className="font-extrabold text-sm text-slate-800 flex items-center gap-1.5">
                  <HelpCircle className="w-4 h-4 text-primary" />
                  Exercícios (JSON Array)
                </h3>
              </div>
              
              <p className="text-[10px] text-slate-400 font-medium leading-relaxed">
                Tipos: multiple_choice (requer options), fill_blank, transformation. Exemplo:
              </p>
              <pre className="p-2.5 rounded-lg bg-slate-50 border border-slate-100 text-[9px] font-mono text-slate-500 overflow-x-auto leading-normal">
{`[
  {
    "type": "multiple_choice",
    "prompt": "Question...",
    "options": ["A", "B"],
    "answer": "A",
    "explanation": "Exp..."
  },
  {
    "type": "fill_blank",
    "prompt": "Je [suis] fatigué.",
    "answer": "suis",
    "explanation": "Exp..."
  }
]`}
              </pre>

              <div className="space-y-1.5">
                <Label htmlFor="exercisesJson" className="text-[10px] font-extrabold text-slate-500">Editor JSON Exercícios</Label>
                <Textarea
                  id="exercisesJson"
                  value={exercisesJson}
                  onChange={e => setExercisesJson(e.target.value)}
                  rows={12}
                  className="rounded-xl font-mono text-[11px] leading-normal border-slate-200 focus:ring-primary"
                />
              </div>
            </Card>

            {/* JSON Dialogue Card */}
            <Card className="border border-slate-100 shadow-xs rounded-2xl p-6 bg-white space-y-4">
              <div className="border-b border-slate-50 pb-2 flex items-center justify-between">
                <h3 className="font-extrabold text-sm text-slate-800 flex items-center gap-1.5">
                  <FileText className="w-4 h-4 text-primary" />
                  Diálogo (JSON Object / null)
                </h3>
              </div>
              
              <p className="text-[10px] text-slate-400 font-medium leading-relaxed">
                Estrutura de diálogo opcional (ou null):
              </p>
              <pre className="p-2.5 rounded-lg bg-slate-50 border border-slate-100 text-[9px] font-mono text-slate-500 overflow-x-auto leading-normal">
{`{
  "title": "Au restaurant",
  "context": "Commander un repas",
  "characters": ["Serveur", "Client"],
  "lines": [
    { "speaker": "Client", "text": "Une table s'il vous plaît" }
  ],
  "vocabularyNotes": [
    { "term": "s'il vous plaît", "meaning": "por favor" }
  ]
}`}
              </pre>

              <div className="space-y-1.5">
                <Label htmlFor="dialogueJson" className="text-[10px] font-extrabold text-slate-500">Editor JSON Diálogo</Label>
                <Textarea
                  id="dialogueJson"
                  value={dialogueJson}
                  onChange={e => setDialogueJson(e.target.value)}
                  rows={10}
                  className="rounded-xl font-mono text-[11px] leading-normal border-slate-200 focus:ring-primary"
                />
              </div>
            </Card>

            {/* JSON Flashcards Card */}
            <Card className="border border-slate-100 shadow-xs rounded-2xl p-6 bg-white space-y-4">
              <div className="border-b border-slate-50 pb-2 flex items-center justify-between">
                <h3 className="font-extrabold text-sm text-slate-800 flex items-center gap-1.5">
                  <FileJson className="w-4 h-4 text-primary" />
                  Flashcards (JSON Array)
                </h3>
              </div>
              
              <p className="text-[10px] text-slate-400 font-medium leading-relaxed">
                Array de flashcards associados:
              </p>
              <pre className="p-2.5 rounded-lg bg-slate-50 border border-slate-100 text-[9px] font-mono text-slate-500 overflow-x-auto leading-normal">
{`[
  {
    "front": "Le pain",
    "back": "O pão",
    "category": "Nourriture",
    "difficulty": "easy"
  }
]`}
              </pre>

              <div className="space-y-1.5">
                <Label htmlFor="flashcardsJson" className="text-[10px] font-extrabold text-slate-500">Editor JSON Flashcards</Label>
                <Textarea
                  id="flashcardsJson"
                  value={flashcardsJson}
                  onChange={e => setFlashcardsJson(e.target.value)}
                  rows={10}
                  className="rounded-xl font-mono text-[11px] leading-normal border-slate-200 focus:ring-primary"
                />
              </div>
            </Card>

          </div>
        </div>

      )}

      {/* 3. BATCH IMPORT MODAL */}
      <Dialog open={isImportOpen} onOpenChange={setIsImportOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto rounded-2xl border-none p-6 text-left">
          <DialogHeader>
            <DialogTitle className="text-xl font-black text-slate-900 flex items-center gap-2">
              <Upload className="w-5 h-5 text-primary" />
              Importar Lições de Gramática em Lote
            </DialogTitle>
            <DialogDescription className="text-xs font-semibold text-slate-500 mt-1">
              Cole um payload JSON contendo uma lista de lições para importá-las diretamente no Supabase.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 my-4">
            {/* Step 1: Textarea */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="importJson" className="text-xs font-bold text-slate-700">Payload JSON *</Label>
                <Button 
                  type="button" 
                  variant="ghost" 
                  onClick={() => {
                    const template = [
                      {
                        "order_index": 1,
                        "level": "A1",
                        "title": "Le présent de l’indicatif",
                        "slug": "present-indicatif",
                        "description": "Comprendre l’usage du présent en français.",
                        "category": "Verbes",
                        "is_published": true,
                        "content": {
                          "intro": "Explication générale...",
                          "rule": "Règles grammaticales...",
                          "examples": ["Exemple A", "Exemple B"],
                          "commonMistakes": [
                            {
                              "incorrect": "Mauvaise forma",
                              "correct": "Boa forma",
                              "explanation": "Explicação..."
                            }
                          ],
                          "tefTip": "Dica para o TEF..."
                        },
                        "exercises": [
                          {
                            "type": "multiple_choice",
                            "prompt": "Pergunta?",
                            "options": ["Opção A", "Opção B", "Opção C", "Opção D"],
                            "answer": "Opção A",
                            "explanation": "Explicação..."
                          }
                        ]
                      }
                    ];
                    setImportJson(JSON.stringify(template, null, 2));
                  }}
                  className="h-6 text-[10px] font-extrabold text-primary hover:text-primary hover:bg-slate-50 gap-1"
                >
                  <FileJson className="w-3 h-3" />
                  Carregar Template Exemplo
                </Button>
              </div>
              <Textarea
                id="importJson"
                placeholder='Ex: [ { "title": "...", "slug": "...", ... } ]'
                value={importJson}
                onChange={e => {
                  setImportJson(e.target.value);
                  setHasValidated(false);
                  setParsedLessons([]);
                  setValidationErrors([]);
                }}
                rows={10}
                className="font-mono text-xs leading-normal rounded-xl border-slate-200 focus:ring-primary"
              />
            </div>

            {/* Option to save to Expression Orale */}
            <div className="flex items-center gap-3 bg-slate-50 p-3.5 rounded-xl border border-slate-100">
              <input
                id="importAsExpressionOrale"
                type="checkbox"
                checked={importAsExpressionOrale}
                onChange={e => setImportAsExpressionOrale(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
              />
              <Label htmlFor="importAsExpressionOrale" className="text-xs font-extrabold text-slate-700 cursor-pointer select-none">
                Importar como lição de "Expression Orale" (Vocabulário de Expressão Oral)
              </Label>
            </div>

            {/* Step 3: Errors and Preview Panel */}
            {hasValidated && (
              <div className="space-y-3">
                <h4 className="font-extrabold text-xs text-slate-800 border-b border-slate-100 pb-1 flex items-center gap-1.5">
                  Resultado da Validação
                  {validationErrors.length === 0 ? (
                    <Badge className="bg-emerald-100 text-emerald-800 border-none text-[9px] font-black px-1.5 py-0.5 rounded hover:bg-emerald-100">Válido</Badge>
                  ) : (
                    <Badge className="bg-red-100 text-red-800 border-none text-[9px] font-black px-1.5 py-0.5 rounded hover:bg-red-100">Inválido</Badge>
                  )}
                </h4>

                {validationErrors.length > 0 ? (
                   <div className="p-3.5 rounded-xl bg-red-50/50 border border-red-100 space-y-2 max-h-48 overflow-y-auto">
                    {validationErrors.map((err, i) => (
                      <div key={i} className="text-xs font-semibold text-red-700 flex items-start gap-2 leading-relaxed">
                        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                        <pre className="font-sans whitespace-pre-wrap">{err}</pre>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-[10px] text-slate-400 font-extrabold uppercase tracking-wide">Pré-visualização das Lições ({parsedLessons.length}):</p>
                    <div className="grid gap-2 max-h-56 overflow-y-auto pr-1">
                      {parsedLessons.map((item, idx) => {
                        const slugExists = lessons.some(l => l.slug === item.slug);
                        return (
                          <div key={idx} className="p-3 rounded-xl border border-slate-100 bg-white shadow-2xs flex flex-col gap-1 text-xs font-semibold">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-extrabold text-slate-900">{item.title}</span>
                              <Badge className={`text-[9px] font-black uppercase border-none px-1.5 py-0.5 rounded ${
                                item.level.startsWith("A") ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-50" :
                                item.level.startsWith("B1") ? "bg-blue-50 text-blue-700 hover:bg-blue-50" : "bg-purple-50 text-purple-700 hover:bg-purple-50"
                              }`}>
                                {item.level}
                              </Badge>
                            </div>
                            <div className="text-slate-400 font-mono text-[9px] flex items-center gap-1.5 flex-wrap">
                              <span>Slug: /{item.slug}</span>
                              <span>•</span>
                              <span>Ordem: {item.order_index}</span>
                              <span>•</span>
                              <span>Exercícios: {item.exercises.length}</span>
                              <span>•</span>
                              <span>Categoria: {importAsExpressionOrale ? "Expression Orale" : (item.category || "Sem Categoria")}</span>
                            </div>
                            {slugExists && (
                              <div className="mt-1 flex items-center gap-1 text-red-700 bg-red-50 border border-red-100 p-1.5 rounded-lg text-[9px] font-medium leading-relaxed">
                                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                                <span>Slug já cadastrado. Importação bloqueada.</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="border-t border-slate-100 pt-4 flex gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setIsImportOpen(false);
                setImportJson("");
                setParsedLessons([]);
                setValidationErrors([]);
                setHasValidated(false);
                setImportAsExpressionOrale(false);
              }}
              className="rounded-xl border border-slate-200 text-slate-700 font-bold text-xs h-10 px-4"
            >
              Cancelar
            </Button>
            {!hasValidated || validationErrors.length > 0 ? (
              <Button
                type="button"
                onClick={handleValidateImport}
                disabled={!importJson.trim()}
                className="rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs h-10 px-4"
              >
                Validar Estrutura
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleImportSubmit}
                disabled={importing || parsedLessons.length === 0}
                className="rounded-xl bg-primary hover:bg-primary/95 text-white font-bold text-xs h-10 px-4 gap-1.5 shadow-sm"
              >
                {importing ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Importando...
                  </>
                ) : (
                  <>
                    <Check className="w-3.5 h-3.5" />
                    Confirmar Importação ({parsedLessons.length})
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

